<template>
  <component :is="component" v-bind="bindings" class="c-button" :class="classes">
    <slot />
  </component>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { RouteLocationRaw, RouterLink, useRouter } from 'vue-router';
  import { RouteActivationStrategy, routeIsActive } from '@/general/router/util';
  import { isEnumValue, isPresent } from '../../utils/typescript';

  export type Type = 'button' | 'submit' | 'reset' | 'container';

  export type Theme =
    | 'main-primary'
    | 'main-secondary'
    | 'main-info'
    | 'main-success'
    | 'main-warning'
    | 'main-danger'
    | 'normal-primary'
    | 'normal-secondary'
    | 'normal-neutral'
    | 'normal-info'
    | 'normal-success'
    | 'normal-warning'
    | 'normal-danger'
    | 'light-primary'
    | 'light-secondary'
    | 'light-info'
    | 'light-success'
    | 'light-warning'
    | 'light-danger'
    | 'ghost-primary'
    | 'ghost-secondary'
    | 'ghost-secondary-light'
    | 'ghost-secondary-medium'
    | 'ghost-info'
    | 'ghost-success'
    | 'ghost-warning'
    | 'ghost-danger'
    | 'ghost-inverse'
    | 'ghost-contrast'
    | 'link'
    | 'naked';

  const props = withDefaults(
    defineProps<{
      type?: Type;
      link?: RouteLocationRaw;
      theme?: Theme;
      title?: string;
      href?: string;
      disabled?: boolean;
      active?: boolean | RouteActivationStrategy;
      fontWeight?: 'normal' | 'bold';
      height?: 'normal' | 'small' | 'xsmall';
    }>(),
    {
      type: 'button',
      theme: 'normal-secondary',
      active: RouteActivationStrategy.FULL_PATH,
    },
  );

  const emit = defineEmits<{
    (e: 'click', v: Event): void;
  }>();

  const router = useRouter();

  const bindings = computed(() => ({
    title: props.title,
    type: props.type,
    to: props.link,
    ...(props.href
      ? {
        href: props.href,
        target: '_blank',
      }
      : {}),
    onClick: (e: Event) => props.disabled || emit('click', e),
  }));

  const component = computed(() => {
    if (props.type === 'container') {
      return 'div';
    } else if (props.link) {
      return RouterLink;
    } else if (props.href) {
      return 'a';
    } else {
      return 'button';
    }
  });

  const classes = computed(() =>
    [
      `c-button-${props.theme}`,
      props.theme.includes('-') && `c-button-${props.theme.split('-')[0]}`,
      props.disabled && 'c-button-disabled',
      isActive() && 'c-button-active',
      `c-button-${props.type}`,
      props.fontWeight && `c-button-font-weight-${props.fontWeight}`,
      props.height && `c-button-${props.height}`,
    ].filter(Boolean),
  );

  function isActive(): boolean {
    if (isEnumValue(props.active, RouteActivationStrategy)) {
      return (
        isPresent(props.link)
        && routeIsActive(router.resolve(props.link), router, props.active)
      );
    }
    return !!props.active; // intentional coercion to allow some flexibility in expressing the prop value
  }
</script>

<style lang="scss">
.c-button {
  $padding-x: 0.75rem;
  $padding-y: 0.5rem;
  $border-size: 1px;

  @mixin main($bg, $bgHover) {
    color: $neutral-0;
    background-color: $bg;
    padding: calc(#{$padding-y} - #{$border-size}) calc(#{$padding-x} - #{$border-size});

    &:hover:not(.c-button-disabled) {
      color: $neutral-0;
      background-color: $bgHover;
    }

    &.c-button-disabled {
      background-color: $disabled-fg;
    }
  }

  @mixin normal($fg, $bgHover, $border, $borderHover: $border) {
    color: $fg;
    background-color: $main-bg;
    border: $border-size solid $border;
    padding: $padding-y $padding-x;

    &:hover:not(.c-button-disabled) {
      color: $fg;
      background-color: $bgHover;
      border-color: $borderHover;
    }

    &.c-button-disabled {
      color: $disabled-fg;
      background-color: $disabled-bg;
    }
  }

  @mixin light($bg, $border) {
    color: $main-fg;
    background-color: $bg;
    border: $border-size solid $border;
    padding: $padding-y $padding-x;

    &:hover:not(.c-button-disabled) {
      background-color: mix($bg, $main-bg, 50%);
    }

    &.c-button-disabled {
      color: $disabled-fg;
      background-color: $disabled-bg;
    }
  }

  @mixin ghost($fg) {
    color: $fg;
    padding: $padding-y;

    &:hover:not(.c-button-disabled) {
      color: $fg;
      background-color: rgba($fg, 0.1);
    }

    &.c-button-disabled {
      color: $disabled-fg;
    }
  }

  font-size: 1rem;
  display: inline-flex;
  vertical-align: middle;
  justify-content: center;
  align-items: center;
  text-align: left;
  line-height: 1.125;
  gap: 0.5rem;
  font-family: inherit;
  font-weight: $font-weight-bold;
  border-radius: $border-radius;
  box-sizing: border-box;
  cursor: pointer;
  border: $border-size solid transparent; // to prevent jumping when button changes theme
  background: none;

  &:focus {
    outline: 0;
  }

  &-main-primary {
    @include main($primary-600, $primary-700);
  }

  &-main-secondary {
    @include main($secondary-600, $secondary-700);
  }

  &-main-info {
    @include main($info-600, $info-700);
  }

  &-main-success {
    @include main($success-600, $success-700);
  }

  &-main-warning {
    @include main($warning-600, $warning-700);
  }

  &-main-danger {
    @include main($danger-600, $danger-700);
  }

  &-normal-primary {
    @include normal($primary-700, $neutral-50, $border-color);
  }

  &-normal-secondary {
    @include normal($neutral-700, $neutral-50, $border-color);
  }

  &-normal-neutral {
    @include normal($neutral-900, $neutral-50, $border-color);
  }

  &-normal-info {
    @include normal($info-700, $info-100, $neutral-400, $info-300);
  }

  &-normal-success {
    @include normal($success-700, $success-100, $neutral-400, $success-300);
  }

  &-normal-warning {
    @include normal($warning-700, $warning-100, $neutral-400, $warning-300);
  }

  &-normal-danger {
    @include normal($danger-700, $danger-100, $neutral-400, $danger-300);
  }

  &-light-primary {
    @include light($primary-100, $primary-300);
  }

  &-light-secondary {
    @include light($secondary-100, $secondary-300);
  }

  &-light-info {
    @include light($info-100, $info-300);
  }

  &-light-success {
    @include light($success-100, $success-300);
  }

  &-light-warning {
    @include light($warning-100, $warning-300);
  }

  &-light-danger {
    @include light($danger-100, $danger-300);
  }

  &-ghost-primary {
    @include ghost($primary-700);
  }

  &-ghost-secondary {
    @include ghost($neutral-900);
  }

  &-ghost-secondary-medium {
    @include ghost($neutral-700);
  }

  &-ghost-secondary-light {
    @include ghost($neutral);
  }

  &-ghost-info {
    @include ghost($info-700);
  }

  &-ghost-success {
    @include ghost($success-700);
  }

  &-ghost-warning {
    @include ghost($warning-700);
  }

  &-ghost-danger {
    @include ghost($danger-700);
  }

  &-ghost-inverse {
    @include ghost($neutral-100);
  }

  &-ghost-contrast {
    @include ghost($neutral-0);
  }

  &-link {
    color: $primary-700;
    padding: 0;

    &:hover:not(.c-button-disabled) {
      color: $primary-700;
      text-decoration: underline;
    }

    &.c-button-disabled {
      color: $disabled-fg;
    }
  }

  &-disabled {
    cursor: not-allowed;

    &.c-button-submit {
      pointer-events: none; // TODO @jan check if still needed with CForm
    }
  }

  // active state styling currently only supports normal theme
  &-active#{&}-normal {
    &,
    &:hover {
      background-color: $primary-100;
    }
  }
}

.c-button-font-weight-normal {
  font-weight: $font-weight-normal;
}

.c-button-small {
  height: 2.25rem;
}

.c-button-xsmall {
  height: 1.5rem;
  font-size: 0.875rem;
  font-family: $font-main;
}
</style>
